import { Building2, Package, PackageCheck } from 'lucide-react'

import {
  calculateOrderTotal,
  formatClinicPrice,
  formatName,
  getClinic,
  type OrderWithAllocations,
  type TreatmentWithRelations,
} from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { OrderStatusBadge } from '@/frontend/features/orders/order-badges'

export function TreatmentOrders({ treatment }: { treatment: TreatmentWithRelations }) {
  if (treatment.orders.length === 0) return null

  return (
    <ul className="flex flex-col gap-2 border-l pl-2.5 text-sm">
      {treatment.orders.map((order) => {
        return (
          <li key={order.id} className="relative flex items-center justify-between gap-3">
            <span aria-hidden className="absolute top-1/2 -left-2.5 h-px w-1.5 bg-border" />
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                <FormattedDate value={order.createdAt} clinicId={order.clinicId} style="numeric" />
              </span>
              {getClinic(order.clinicId) && (
                <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Building2 className="size-3 shrink-0" />
                  {getClinic(order.clinicId)?.name}
                </span>
              )}
              {(order.preparedAt || order.dispensedAt) && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground font-medium tabular-nums">
                  <OrderEvent
                    label="Prepared"
                    date={order.preparedAt}
                    clinicId={order.clinicId}
                    staff={order.preparedByStaff}
                  />
                  <OrderEvent
                    label="Dispensed"
                    date={order.dispensedAt}
                    clinicId={order.clinicId}
                    staff={order.dispensedByStaff}
                  />
                </div>
              )}
              <OrderStatusBadge status={order.status} size="xs" title={order.notes ?? undefined} />
            </div>
            <OrderPrice order={order} />
          </li>
        )
      })}
    </ul>
  )
}

function OrderEvent({
  label,
  date,
  clinicId,
  staff,
}: {
  label: 'Prepared' | 'Dispensed'
  date: string | null
  clinicId: string
  staff?: OrderWithAllocations['preparedByStaff']
}) {
  if (!date) return null
  const Icon = { Prepared: Package, Dispensed: PackageCheck }[label]

  return (
    <span className="inline-flex items-center gap-0.75 whitespace-nowrap">
      <Icon className="size-2.5 shrink-0" />
      {label} <FormattedDate value={date} clinicId={clinicId} style="numeric" />
      {staff && (
        <span title={formatName(staff)}>
          by {formatName({ firstName: staff.firstName, lastName: staff.lastName?.[0] })}
        </span>
      )}
    </span>
  )
}

/** Fill prices use the order's snapshotted pricing and billing clinic's currency. */
function OrderPrice({ order }: { order: OrderWithAllocations }) {
  if (!getClinic(order.clinicId)) return null

  return (
    <span className="flex shrink-0 items-center gap-2 tabular-nums">
      {order.discountBps > 0 && (
        <>
          <Badge variant="neutral" size="sm">
            {order.discountBps / 100}% off
          </Badge>
          <s className="text-muted-foreground">
            {formatClinicPrice(calculateOrderTotal({ ...order, discountBps: 0 }), order.clinicId)}
          </s>
        </>
      )}
      <span className="font-semibold text-foreground">
        {formatClinicPrice(order.totalPrice, order.clinicId)}
      </span>
    </span>
  )
}
